import type { NodeKind } from '@/flow/types';
import { getNodeDisplayLabel } from '@/flow/displayLanguage';
import type {
  LearningDifficulty,
  LearningInitialCard,
  LearningLevel,
  LearningProblem,
} from './learningTypes';

const ALL_LEARNING_CARD_KINDS: NodeKind[] = [
  'textEdit', 'sayText', 'gesture', 'customGesture', 'dance', 'qrScan', 'tof', 'listen',
  'if', 'switch', 'counter', 'coinFlip', 'wait', 'randomChoice', 'keywordSwitch',
  'soundEffect', 'light', 'function', 'walking',
];

const WITHOUT_FUNCTION = ALL_LEARNING_CARD_KINDS.filter((kind) => kind !== 'function');

type ExecutionRequirement = {
  nodeKind: NodeKind;
  minimumCount?: number;
};

type ProblemSeed = {
  title: string;
  description: string;
  cards: LearningInitialCard[];
  executionRequirements?: ExecutionRequirement[];
  speech?: string;
  goalReached?: boolean;
};

const problemSeeds: Record<number, ProblemSeed> = {
  1: {
    title: 'こんにちは',
    description: '「こんにちは」と話すように、カードを線でつないでください。',
    cards: [
      { nodeKind: 'textEdit', data: { text: 'こんにちは' } },
      { nodeKind: 'sayText' },
    ],
    speech: 'こんにちは',
  },
  2: {
    title: '一歩すすもう',
    description: 'ロボットが赤いゴールマスまで前に進むように、カードを線でつないでください。',
    cards: [{ nodeKind: 'walking', data: { motionId: '4', stepCount: 4 } }],
    goalReached: true,
  },
  3: {
    title: '動いてあいさつ',
    description: '赤いゴールマスまで進んでから「やったね」と話すように、カードを線でつないでください。',
    cards: [
      { nodeKind: 'walking', data: { motionId: '4', stepCount: 4 } },
      { nodeKind: 'textEdit', data: { text: 'やったね' } },
      { nodeKind: 'sayText' },
    ],
    speech: 'やったね',
    goalReached: true,
  },
  4: {
    title: '合図を待とう',
    description: '「よーい」と話し、1秒待ってから前に進むように、カードを線でつないでください。',
    cards: [
      { nodeKind: 'textEdit', data: { text: 'よーい' } },
      { nodeKind: 'sayText' },
      { nodeKind: 'wait', data: { seconds: 1 } },
      { nodeKind: 'walking', data: { motionId: '4', stepCount: 4 } },
    ],
    speech: 'よーい',
  },
  5: {
    title: '角をまがろう',
    description: '前に進み、右を向いて、もう一度前に進むように、カードを線でつないでください。',
    cards: [
      { nodeKind: 'walking', data: { motionId: '4', stepCount: 4 } },
      { nodeKind: 'walking', data: { motionId: '8' } },
      { nodeKind: 'walking', data: { motionId: '4', stepCount: 4 } },
    ],
    goalReached: true,
  },
  6: {
    title: '青くぴかぴか',
    description: '青く光ったあと、1秒待つように、カードを線でつないでください。ライトはカードの終了時に自動で消えます。',
    cards: [
      { nodeKind: 'light', data: { color: 'blue' } },
      { nodeKind: 'wait', data: { seconds: 1 } },
    ],
  },
  7: {
    title: '色をえらぼう',
    description: 'コイントスカードで「あお」か「あか」をランダムにえらび、「あお」なら青、「あか」なら赤く光るように、カードを線でつないでください。',
    cards: [
      { nodeKind: 'coinFlip' },
      { nodeKind: 'textEdit', data: { text: 'あお' } },
      { nodeKind: 'textEdit', data: { text: 'あか' } },
      { nodeKind: 'switch', data: { matchValue1: 'あお', matchValue2: 'あか' } },
      { nodeKind: 'light', data: { color: 'blue' } },
      { nodeKind: 'light', data: { color: 'red' } },
    ],
    executionRequirements: [
      { nodeKind: 'coinFlip' },
      { nodeKind: 'textEdit' },
      { nodeKind: 'switch' },
      { nodeKind: 'light' },
    ],
  },
  8: {
    title: 'かべを調べよう',
    description: '距離が0なら止まり、700mm以内なら右を向き、それより遠ければ一歩進むように、カードを線でつないでください。',
    cards: [
      { nodeKind: 'tof' },
      { nodeKind: 'if', data: { operator: '=', compareValue: '0' } },
      { nodeKind: 'if', data: { operator: '<=', compareValue: '700' } },
      { nodeKind: 'walking', data: { motionId: '8' } },
      { nodeKind: 'walking', data: { motionId: '4', stepCount: 1 } },
    ],
    executionRequirements: [
      { nodeKind: 'tof' },
      { nodeKind: 'if', minimumCount: 2 },
      { nodeKind: 'walking' },
    ],
  },
  9: {
    title: '二回ぴかぴか',
    description: '「青く光る、1秒待つ」を2回くり返すように、カードを線でつないでください。ライトはカードの終了時に自動で消えます。',
    cards: [
      { nodeKind: 'counter', data: { initialValue: 0, stepValue: 1, finalValue: 1 } },
      { nodeKind: 'light', data: { color: 'blue' } },
      { nodeKind: 'wait', data: { seconds: 1 } },
    ],
    executionRequirements: [
      { nodeKind: 'counter', minimumCount: 3 },
      { nodeKind: 'light', minimumCount: 2 },
      { nodeKind: 'wait', minimumCount: 2 },
    ],
  },
  10: {
    title: 'パトロール開始',
    description: '「パトロール」のときは三歩進み、効果音を鳴らして緑に光るように、カードを線でつないでください。',
    cards: [
      { nodeKind: 'textEdit', data: { text: 'パトロール' } },
      { nodeKind: 'keywordSwitch', data: { keyword1: 'パトロール', keyword2: 'ストップ' } },
      { nodeKind: 'counter', data: { initialValue: 1, stepValue: 1, finalValue: 3 } },
      { nodeKind: 'walking', data: { motionId: '4', stepCount: 1 } },
      { nodeKind: 'soundEffect', data: { sound: 'found' } },
      { nodeKind: 'light', data: { color: 'green' } },
    ],
    executionRequirements: [
      { nodeKind: 'textEdit' },
      { nodeKind: 'keywordSwitch' },
      { nodeKind: 'counter', minimumCount: 4 },
      { nodeKind: 'walking', minimumCount: 3 },
      { nodeKind: 'soundEffect' },
      { nodeKind: 'light' },
    ],
  },
};

const difficultyForLevel = (level: number): LearningDifficulty => {
  if (level <= 3) return 'basic';
  if (level <= 6) return 'beginner';
  if (level <= 9) return 'intermediate';
  return 'advanced';
};

const descriptionForLevel = (level: number) => {
  if (level <= 3) return '基礎';
  if (level <= 6) return '初級';
  if (level <= 9) return '中級';
  return '上級';
};

const cardsUnlockedThroughLevel = (level: number): NodeKind[] => {
  if (level >= 10) return ALL_LEARNING_CARD_KINDS;
  if (level === 9) return WITHOUT_FUNCTION;
  const introduced = Object.entries(problemSeeds)
    .filter(([candidate]) => Number(candidate) <= level)
    .flatMap(([, seed]) => seed.cards.map(({ nodeKind }) => nodeKind));
  return [...new Set(introduced)];
};

const defaultExecutionRequirements = (cards: LearningInitialCard[]): ExecutionRequirement[] => (
  Object.entries(cards.reduce<Partial<Record<NodeKind, number>>>((counts, nodeKind) => {
    counts[nodeKind.nodeKind] = (counts[nodeKind.nodeKind] ?? 0) + 1;
    return counts;
  }, {})).map(([nodeKind, minimumCount]) => ({
    nodeKind: nodeKind as NodeKind,
    minimumCount,
  }))
);

const createProblem = (level: number, seed: ProblemSeed): LearningProblem => {
  const id = `L${level}-1`;
  const nodeKinds = seed.cards.map(({ nodeKind }) => nodeKind);
  const nodeDisplayLabels = nodeKinds.map((nodeKind) => getNodeDisplayLabel(nodeKind, 'ja'));
  const startLabel = getNodeDisplayLabel('start', 'ja');
  const endLabel = getNodeDisplayLabel('end', 'ja');
  const executionRequirements = seed.executionRequirements ?? defaultExecutionRequirements(seed.cards);
  const goals = [
    { type: 'terminalReached' as const },
    ...executionRequirements.map(({ nodeKind, minimumCount }) => ({
      type: 'nodeExecuted' as const,
      nodeKind,
      minimumCount,
    })),
    ...(seed.speech ? [{ type: 'speech' as const, text: seed.speech, match: 'exact' as const }] : []),
    ...(seed.goalReached ? [{ type: 'goalReached' as const }, { type: 'noCollision' as const }] : []),
  ];

  return {
    id,
    levelId: `level-${level}`,
    order: 1,
    title: seed.title,
    description: seed.description,
    availableNodeKinds: cardsUnlockedThroughLevel(level),
    initialCards: seed.cards,
    requiredNodeKinds: nodeKinds,
    goals,
    hints: [
      `まず、${nodeDisplayLabels[0]}カードから考えてみましょう。`,
      `使うカードは ${nodeDisplayLabels.join('、')} です。`,
      level >= 9
        ? `くり返す線は、最後の動作から${getNodeDisplayLabel('counter', 'ja')}の「もどる」につなぎましょう。`
        : `${startLabel}から${endLabel}まで、動かしたい順に線でつないでみましょう。`,
    ],
    optimalSolutions: [{
      nodeKinds,
      explanation: `${nodeDisplayLabels.join(' → ')} の順を基本に、分かれ道やくり返しをつなぎます。`,
      expectedExecutedNodeCount: executionRequirements.reduce(
        (count, requirement) => count + (requirement.minimumCount ?? 1),
        2,
      ),
    }],
    successMessage: `${seed.title}をクリアしました！`,
    stageId: Math.min(3, Math.max(1, Math.ceil(level / 4))),
  };
};

export const LEARNING_LEVELS: LearningLevel[] = Object.entries(problemSeeds).map(([levelKey, seed]) => {
  const level = Number(levelKey);
  return {
    id: `level-${level}`,
    order: level,
    title: `Level ${level}`,
    description: descriptionForLevel(level),
    difficulty: difficultyForLevel(level),
    problems: [createProblem(level, seed)],
  };
});

export const LEARNING_PROBLEMS = LEARNING_LEVELS.flatMap((level) => level.problems);
export const LEARNING_CARD_KINDS = ALL_LEARNING_CARD_KINDS;

export const getLearningProblem = (problemId: string) => (
  LEARNING_PROBLEMS.find((problem) => problem.id === problemId) ?? LEARNING_PROBLEMS[0]
);
